// Types
import { AIStatementReaderMultiPortfolioState, MultiPortfolioData } from './types/ai-statement-reader-multi-portfolio';
import { Action, AsyncResult } from '../index';

// Actions
import { batchAddHoldingsToPortfolio, resetPortfolio, setNewPortfolioId, setPortfolioName } from './edit-portfolio';
import { isNil } from 'lodash';
import { formatMessage } from '@/utils/i18n';

const initialState: AIStatementReaderMultiPortfolioState = {
  portfolios: [],
  jobId: null,
  isProcessingError: false,
  savedPortfolioIds: [],
};

// Actions
export const STORE_MULTI_PORTFOLIO_RESULTS: Action = {
  type: 'connect/ai-statement-reader-multi-portfolio/store-results',
};

export const SET_JOB_ID: Action = {
  type: 'connect/ai-statement-reader-multi-portfolio/set-job-id',
};

export const RESET_MULTI_PORTFOLIO: Action = {
  type: 'connect/ai-statement-reader-multi-portfolio/reset',
};

export const SET_IS_PROCESSING_ERROR: Action = {
  type: 'connect/ai-statement-reader-multi-portfolio/set-is-processing-error',
};

export const SET_CURRENT_PORTFOLIO_INDEX: Action = {
  type: 'connect/ai-statement-reader-multi-portfolio/set-current-portfolio-index',
};

export const ADD_SAVED_PORTFOLIO_ID: Action = {
  type: 'connect/ai-statement-reader-multi-portfolio/add-saved-portfolio-id',
};

// Action Creators
export const storeMultiPortfolioResults = (portfolios: MultiPortfolioData[]) => ({
  type: STORE_MULTI_PORTFOLIO_RESULTS.type,
  portfolios,
});

export const setJobId = (jobId: string | null) => ({
  type: SET_JOB_ID.type,
  jobId,
});

export const resetMultiPortfolio = () => ({
  type: RESET_MULTI_PORTFOLIO.type,
});

export const setIsProcessingError = (isProcessingError: boolean) => ({
  type: SET_IS_PROCESSING_ERROR.type,
  isProcessingError,
});

export const setCurrentPortfolioIndex = (index: number) => ({
  type: SET_CURRENT_PORTFOLIO_INDEX.type,
  index,
});

export const addSavedPortfolioId = (portfolioId: string) => ({
  type: ADD_SAVED_PORTFOLIO_ID.type,
  portfolioId,
});

// Reducer
export default function reducer(
  state = initialState,
  action: Action & { portfolios?: MultiPortfolioData[]; jobId?: string | null; isProcessingError?: boolean; index?: number },
) {
  switch (action.type) {
    case STORE_MULTI_PORTFOLIO_RESULTS.type: {
      return {
        ...state,
        portfolios: action.portfolios || [],
        isProcessingError: false,
      };
    }
    case SET_JOB_ID.type: {
      return {
        ...state,
        jobId: action.jobId || null,
      };
    }
    case ADD_SAVED_PORTFOLIO_ID.type: {
      return {
        ...state,
        savedPortfolioIds: [...(state.savedPortfolioIds || []), action.portfolioId],
      };
    }
    case RESET_MULTI_PORTFOLIO.type: {
      return {
        ...initialState,
      };
    }
    case SET_IS_PROCESSING_ERROR.type: {
      return {
        ...state,
        isProcessingError: action.isProcessingError || false,
      };
    }
    case SET_CURRENT_PORTFOLIO_INDEX.type: {
      return {
        ...state,
        currentPortfolioIndex: action.index,
      };
    }
    default:
      return state;
  }
}

// Selectors
export const hasNextPortfolio = (state: AIStatementReaderMultiPortfolioState): boolean => {
  const { portfolios, currentPortfolioIndex } = state;
  return currentPortfolioIndex !== undefined && currentPortfolioIndex + 1 < portfolios.length;
};

export const isInMultiPortfolioMode = (state: AIStatementReaderMultiPortfolioState): boolean => state.currentPortfolioIndex !== undefined;

export const shouldClosePortfolioEditor = (state: AIStatementReaderMultiPortfolioState): boolean => {
  return !isInMultiPortfolioMode(state) || !hasNextPortfolio(state);
};

// Thunks
export const processFirstPortfolio = (): AsyncResult<null | MultiPortfolioData> => async (dispatch, getState) => {
  const { portfolios } = getState().aiStatementReaderMultiPortfolio;

  if (portfolios.length === 0) {
    return null;
  }

  dispatch(setCurrentPortfolioIndex(0));

  // Set a default portfolio name so save button isn't disabled
  const defaultPortfolioName = formatMessage('PortfolioEditorBuilder.Overview.DefaultPortfolioName');
  dispatch(setPortfolioName(`${defaultPortfolioName} 1`));

  // Add holdings to portfolio editor state
  await dispatch(batchAddHoldingsToPortfolio(portfolios[0].holdings));
  return portfolios[0];
};

export const processNextPortfolio = (): AsyncResult<null | MultiPortfolioData> => async (dispatch, getState) => {
  const { portfolios, currentPortfolioIndex } = getState().aiStatementReaderMultiPortfolio;

  if (portfolios.length === 0 || isNil(currentPortfolioIndex)) {
    return null;
  }

  const nextIndex = currentPortfolioIndex + 1;

  // Check if there are more portfolios to process
  if (nextIndex >= portfolios.length) {
    return null;
  }

  // Increment to next portfolio
  dispatch(setCurrentPortfolioIndex(nextIndex));

  // Reset portfolio to initial state but keep modal open (preserves contact automatically)
  dispatch(resetPortfolio(true));

  // // Adding the portfolio ID so we can track it in the comparison
  dispatch(setNewPortfolioId());

  // Set a default portfolio name so save button isn't disabled
  const defaultPortfolioName = formatMessage('PortfolioEditorBuilder.Overview.DefaultPortfolioName');
  dispatch(setPortfolioName(`${defaultPortfolioName} ${nextIndex + 1}`));

  // Add holdings for the next portfolio
  await dispatch(batchAddHoldingsToPortfolio(portfolios[nextIndex].holdings));

  return portfolios[nextIndex];
};
